/**
 * NewsContext - Contexto para gestionar el contador de noticias sin leer
 * Permite que el Sidebar3D muestre el badge actualizado
 */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const NewsContext = createContext(null);

export const NewsProvider = ({ children }) => {
  // Noticias del mercado (todas)
  const [marketNews, setMarketNews] = useState([]);
  
  // IDs de noticias leídas (guardadas en localStorage)
  const [readNewsIds, setReadNewsIds] = useState(() => {
    try {
      const saved = localStorage.getItem('readNewsIds');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Calcular contador de noticias sin leer
  const unreadCount = marketNews.filter(news => {
    const newsId = `${news.ticker}-${news.title?.substring(0, 50)}`;
    return !readNewsIds.includes(newsId);
  }).length;

  // Actualizar la lista de noticias del mercado
  const updateMarketNews = useCallback((news) => {
    setMarketNews(news || []);
  }, []);

  // Marcar una noticia como leída
  const markAsRead = useCallback((newsId) => {
    setReadNewsIds(prev => {
      const updated = [...new Set([...prev, newsId])];
      // Mantener solo las últimas 500 para no llenar localStorage
      const trimmed = updated.slice(-500);
      localStorage.setItem('readNewsIds', JSON.stringify(trimmed));
      return trimmed;
    });
  }, []);

  // Marcar todas las noticias como leídas
  const markAllAsRead = useCallback(() => {
    const allIds = marketNews.map(news => `${news.ticker}-${news.title?.substring(0, 50)}`);
    setReadNewsIds(prev => {
      const updated = [...new Set([...prev, ...allIds])];
      const trimmed = updated.slice(-500);
      localStorage.setItem('readNewsIds', JSON.stringify(trimmed));
      return trimmed;
    });
  }, [marketNews]);

  // Verificar si una noticia está leída
  const isRead = useCallback((newsId) => {
    return readNewsIds.includes(newsId);
  }, [readNewsIds]);

  // Generar ID único para una noticia
  const getNewsId = useCallback((news) => {
    return `${news.ticker}-${news.title?.substring(0, 50)}`;
  }, []);

  // Memoizar el objeto value para evitar re-renders innecesarios
  const value = useMemo(() => ({
    marketNews,
    unreadCount,
    readNewsIds,
    updateMarketNews,
    markAsRead,
    markAllAsRead,
    isRead,
    getNewsId,
  }), [marketNews, unreadCount, readNewsIds, updateMarketNews, markAsRead, markAllAsRead, isRead, getNewsId]);

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
};

export const useNews = () => {
  const context = useContext(NewsContext);
  if (!context) {
    throw new Error('useNews debe usarse dentro de un NewsProvider');
  }
  return context;
};

export default NewsContext;
